import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { RelaySection } from '@/components/RelaySection';
import { SEO } from '@/components/SEO';

const plans = [
  {
    id: 'streamsaver',
    name: 'StreamSaver',
    price: 39.99,
    description: 'Budget-friendly essentials',
    link: 'https://irlhosting.com/whmcs/index.php?rp=/store/streamer-hosting/streamsaver-basic',
    badge: 'Beta',
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 59.99,
    description: 'Perfect for phone streamers',
    link: 'https://irlhosting.com/whmcs/index.php?rp=/store/streamer-hosting/basic',
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    price: 79.99,
    description: 'Full control & customization',
    link: 'https://irlhosting.com/whmcs/index.php?rp=/store/streamer-hosting/streamer',
    popular: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 129.99,
    description: 'Maximum power & support',
    link: 'https://irlhosting.com/whmcs/index.php?rp=/store/streamer-hosting/full-support',
  },
];

const features = [
  {
    name: 'Server Type',
    tooltip: 'The type of server hardware allocated to your stream.',
    values: {
      streamsaver: 'Shared Instance',
      basic: 'Shared VPS',
      intermediate: 'Semi-Dedicated',
      professional: 'Dedicated Bare Metal',
    },
  },
  {
    name: 'Max Resolution',
    tooltip: 'Maximum streaming quality supported by your plan.',
    values: {
      streamsaver: '720p/60fps',
      basic: '1080p/60fps',
      intermediate: '1080p/60fps',
      professional: '4K/60fps',
    },
  },
  {
    name: '24/7 Streaming',
    tooltip: 'Stream continuously without time limits or timeouts.',
    values: {
      streamsaver: true,
      basic: true,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Unlimited Bandwidth',
    tooltip: 'No data caps or overage charges, ever.',
    values: {
      streamsaver: true,
      basic: true,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Disconnect Protection',
    tooltip: 'Auto-reconnect keeps you live. Viewers see a holding scene instead of "offline".',
    values: {
      streamsaver: 'Basic',
      basic: true,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Endpoint Relays',
    tooltip: 'Receive streams from your encoder via SRT, SRTLA, RIST, or RTMP. *Additional relays available for purchase.',
    values: {
      streamsaver: '1',
      basic: '1',
      intermediate: '1*',
      professional: '3*',
    },
  },
  {
    name: 'Direct Remote Desktop Access',
    tooltip: 'Full access to your streaming PC from anywhere via remote desktop.',
    values: {
      streamsaver: false,
      basic: false,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Full OBS Control',
    tooltip: 'Manage OBS settings, scenes, and overlays remotely.',
    values: {
      streamsaver: false,
      basic: false,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Multi-Platform Streaming',
    tooltip: 'Broadcast to Twitch, Kick, YouTube, TikTok and more simultaneously.',
    values: {
      streamsaver: false,
      basic: false,
      intermediate: 'Up to 5',
      professional: 'Up to 12',
    },
  },
  {
    name: 'Collab Stream Management',
    tooltip: 'Manage multiple streamers or camera feeds in one session.',
    values: {
      streamsaver: false,
      basic: false,
      intermediate: false,
      professional: true,
    },
  },
  {
    name: 'IRLremote+ App',
    tooltip: 'Control scenes, sources, and monitor stream health from your phone.',
    values: {
      streamsaver: true,
      basic: true,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Setup & Configuration',
    tooltip: 'We configure your server and help you get started.',
    values: {
      streamsaver: true,
      basic: true,
      intermediate: true,
      professional: true,
    },
  },
  {
    name: 'Custom Idea Implementation',
    tooltip: 'We help implement your unique streaming ideas and requirements.',
    values: {
      streamsaver: false,
      basic: false,
      intermediate: false,
      professional: true,
    },
  },
];

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) {
    return <Check className="h-5 w-5 text-cyan-400 mx-auto" />;
  }
  if (value === false) {
    return <X className="h-5 w-5 text-gray-600 mx-auto" />;
  }
  if (typeof value === 'string' && value.endsWith('*')) {
    const baseValue = value.slice(0, -1);
    return (
      <span className="text-gray-300 text-sm inline-flex items-center gap-1 justify-center">
        {baseValue}
        <span className="relative group">
          <HelpCircle className="h-3.5 w-3.5 text-gray-500 hover:text-gray-400 cursor-help" />
          <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-48 p-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl pointer-events-none">
            Additional relays available for purchase
          </span>
        </span>
      </span>
    );
  }
  return <span className="text-gray-300 text-sm">{value}</span>;
}

export function Pricing() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <SEO 
        title="Pricing"
        description="IRL Hosting pricing plans from $39.99/month. Cloud OBS hosting, SRT/SRTLA/RIST relay endpoints, disconnect protection, multi-platform streaming. Compare Starter, Intermediate & Professional plans."
        canonical="/pricing"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Simple, transparent pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Choose the plan that fits your streaming needs. No setup fees, no hidden costs.
          </motion.p>
        </div>

        {/* Pricing Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="overflow-x-auto pt-10 -mx-4 px-4 md:mx-0 md:px-0"
        >
          <table className="w-full border-collapse min-w-[800px]">
            {/* Header Row */}
            <thead>
              <tr>
                <th className="p-4 text-left text-gray-400 font-medium border-b border-gray-800 min-w-[140px] md:min-w-[200px] sticky left-0 bg-[#0a0a0a] z-10">
                  Features
                </th>
                {plans.map((plan) => (
                  <th
                    key={plan.id}
                    className={`p-4 text-center border-b min-w-[160px] ${
                      plan.popular
                        ? 'border-cyan-500/50 bg-cyan-500/5'
                        : 'border-gray-800'
                    }`}
                  >
                    <div className="relative">
                      {plan.popular && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-medium text-cyan-400 bg-cyan-500/10 px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      )}
                      {plan.badge && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-medium text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full">
                          {plan.badge}
                        </span>
                      )}
                      <div className="font-semibold text-white text-lg">
                        {plan.name}
                      </div>
                      <div className="text-2xl font-bold text-white mt-2">
                        ${plan.price}
                        <span className="text-sm text-gray-500 font-normal">/mo</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {plan.description}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Feature Rows */}
            <tbody>
              {features.map((feature, index) => (
                <tr
                  key={feature.name}
                  className={`${index % 2 === 0 ? 'bg-gray-900/30' : ''} relative hover:z-50`}
                >
                  <td className={`p-4 text-gray-300 text-sm border-b border-gray-800/50 sticky left-0 ${index % 2 === 0 ? 'bg-[#141418]' : 'bg-[#0a0a0a]'}`}>
                    <div className="flex items-center gap-2">
                      {feature.name}
                      {feature.tooltip && (
                        <div className="relative group">
                          <button className="touch-manipulation">
                            <HelpCircle className="h-4 w-4 text-gray-500 hover:text-gray-400 cursor-help" />
                          </button>
                          <div className="absolute left-0 top-full mt-2 md:left-6 md:top-1/2 md:-translate-y-1/2 md:mt-0 w-56 md:w-64 p-3 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all z-50 shadow-xl pointer-events-none">
                            {feature.tooltip}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  {plans.map((plan) => (
                    <td
                      key={`${feature.name}-${plan.id}`}
                      className={`p-4 text-center border-b ${
                        plan.popular
                          ? 'border-gray-800/50 bg-cyan-500/5'
                          : 'border-gray-800/50'
                      }`}
                    >
                      <FeatureValue value={feature.values[plan.id as keyof typeof feature.values]} />
                    </td>
                  ))}
                </tr>
              ))}

              {/* CTA Row */}
              <tr>
                <td className="p-6"></td>
                {plans.map((plan) => (
                  <td
                    key={`cta-${plan.id}`}
                    className={`p-6 text-center ${
                      plan.popular ? 'bg-cyan-500/5' : ''
                    }`}
                  >
                    <a href={plan.link} target="_blank" rel="noopener noreferrer">
                      <Button
                        className={`w-full ${
                          plan.popular
                            ? 'bg-cyan-500 hover:bg-cyan-400 text-black'
                            : 'bg-gray-800 hover:bg-gray-700 text-white'
                        }`}
                      >
                        Get Started
                      </Button>
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 text-sm mb-8">
            All plans include no setup fees. Server delivery within 24 hours (most delivered much sooner).
          </p>

          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold text-white mb-3">
              Need something custom?
            </h3>
            <p className="text-gray-400 mb-6">
              Dedicated GPU servers, fleet deployments, or sub-second latency for events. We build what you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                  Contact on Discord
                </Button>
              </a>
              <a href="mailto:ideas@irlhosting.com">
                <Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Relay Endpoints */}
        <RelaySection />

        {/* FAQ Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <Link
            to="/faq"
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            Have questions? Check our FAQ
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
